// ============================================================
// src/App.jsx — Punto de entrada principal de OpenMind
// Gestiona autenticación y rutas de la aplicación
// ============================================================

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import LevelPage from './components/LevelPage';
import LessonView from './components/LessonView';
import Profile from './components/Profile';
import Navbar from './components/Navbar';

export default function App() {
  // Estado del usuario autenticado
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Escuchar cambios de autenticación en Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    // Limpiar el listener al desmontar
    return unsubscribe;
  }, []);

  // Pantalla de carga mientras Firebase verifica la sesión
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce"><img src="logo.png" alt="" className="w-20 h-20 mx-auto object-contain"/></div>
          <p className="text-3xl font-bold text-indigo-900">OpenMind</p>
          <p className="text-gray-500 mt-2">Cargando tu experiencia...</p>
          <div className="flex justify-center gap-1 mt-4">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {/* Mostrar Navbar solo si hay usuario autenticado */}
      {user && <Navbar user={user} />}

      <Routes>
        {/* Ruta raíz: Dashboard si autenticado, Login si no */}
        <Route
          path="/"
          element={user ? <Dashboard user={user} /> : <Login />}
        />

        {/* Página de niveles de una materia */}
        <Route
          path="/subject/:subject"
          element={user ? <LevelPage user={user} /> : <Navigate to="/" />}
        />

        {/* Vista de una lección específica */}
        <Route
          path="/lesson/:subject/:level/:topicId"
          element={user ? <LessonView user={user} /> : <Navigate to="/" />}
        />

        {/* Perfil del usuario */}
        <Route
          path="/profile"
          element={user ? <Profile user={user} /> : <Navigate to="/" />}
        />

        {/* Ruta por defecto: redirigir al inicio */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}